import { CalendarDays, ClipboardCheck, Clock, GraduationCap } from "lucide-react";
import { Reveal, Overline } from "@/components/site/Reveal";
import { IMAGES } from "@/lib/site";

const weeks = [
  { w: "Week 1", d: "3 classes / subject", note: "Concept building", accent: false },
  { w: "Week 2", d: "4 classes / subject", note: "+ Test & doubt-clearing", accent: true },
  { w: "Week 3", d: "3 classes / subject", note: "Practice & application", accent: false },
  { w: "Week 4", d: "4 classes / subject", note: "+ Assessment & queries", accent: true },
];

const durations = [
  { g: "Class 1–5", t: "1 hour", s: "per subject session" },
  { g: "Class 6–8", t: "1.5 hours", s: "per subject session" },
  { g: "All subjects · 1–2", t: "1.5 hours", s: "5 days a week" },
  { g: "All subjects · 6–8", t: "2.5 hours", s: "5 days a week" },
];

export const HowItWorks = () => (
  <section id="how" className="py-24 md:py-32 max-w-7xl mx-auto px-5 md:px-8">
    <Reveal>
      <Overline>How it works</Overline>
      <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight leading-[1.05] max-w-2xl">
        A rhythm built for real progress, not just more classes.
      </h2>
      <p className="mt-5 text-muted-foreground max-w-xl text-base md:text-lg">
        Every alternate week adds a dedicated session for tests, progress evaluation and clearing your child's doubts. Aligned to your school's syllabus and schedule.
      </p>
    </Reveal>

    <div className="grid md:grid-cols-12 gap-6 mt-14">
      <Reveal className="md:col-span-7" delay={0.05}>
        <div className="bg-white rounded-3xl border border-black/5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-7 md:p-9 h-full">
          <div className="flex items-center gap-3 mb-6">
            <CalendarDays className="text-primary" />
            <h3 className="font-heading font-bold text-2xl">The monthly class cycle</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {weeks.map((wk) => (
              <div key={wk.w} className={`rounded-2xl p-5 border ${wk.accent ? "bg-primary text-primary-foreground border-primary" : "bg-muted border-transparent"}`}>
                <div className={`font-label text-xs tracking-widest uppercase ${wk.accent ? "text-accent" : "text-secondary"}`}>{wk.w}</div>
                <div className="font-heading font-bold text-xl mt-1">{wk.d}</div>
                <div className={`text-sm mt-1 ${wk.accent ? "text-white/80" : "text-muted-foreground"}`}>{wk.note}</div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <Reveal className="md:col-span-5" delay={0.12}>
        <div className="relative rounded-3xl overflow-hidden h-full min-h-[280px]">
          <img src={IMAGES.parent} alt="Parent and child studying together" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-primary/40" />
          <div className="relative p-7 md:p-9 h-full flex flex-col justify-end text-white">
            <GraduationCap size={32} className="mb-3" />
            <h3 className="font-heading font-bold text-2xl leading-tight">Parents stay in the loop, always</h3>
            <p className="text-white/85 text-sm mt-2">Regular assessments and progress updates keep you confident about every rupee spent.</p>
          </div>
        </div>
      </Reveal>
    </div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
      {durations.map((d, i) => (
        <Reveal key={d.g} delay={0.05 * i}>
          <div className="bg-white rounded-3xl border border-black/5 p-6 hover:-translate-y-2 transition-transform duration-300">
            <Clock className="text-secondary mb-3" size={22} />
            <div className="font-label text-xs uppercase tracking-widest text-muted-foreground">{d.g}</div>
            <div className="font-heading font-black text-2xl text-primary mt-1">{d.t}</div>
            <div className="text-sm text-muted-foreground">{d.s}</div>
          </div>
        </Reveal>
      ))}
    </div>

    <Reveal delay={0.1}>
      <div className="mt-6 flex items-start gap-3 rounded-2xl bg-accent/25 border border-accent/40 p-5">
        <ClipboardCheck className="text-primary shrink-0 mt-0.5" />
        <p className="text-sm text-foreground/80">
          <b>Focus on 1-to-1 sessions or small batches.</b> All classes follow the school's prescribed syllabus and academic schedule to ensure timely curriculum completion.
        </p>
      </div>
    </Reveal>
  </section>
);

export default HowItWorks;
